import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { EvidenceChip } from "../components/EvidenceChip";
import AdvisoryNote from "../components/AdvisoryNote";
import ReviewTimeline from "../components/ReviewTimeline";
import KatlasReceiptPanel from "../components/KatlasReceiptPanel";
import ReviewActions from "../components/ReviewActions";
import { ArrowLeft } from "lucide-react";

function LabelValue({ label, value }) {
  return (
    <div>
      <p className="eyebrow">{label}</p>
      <p className="text-[var(--ink-heading)] mt-1.5">{value || <span className="text-[var(--ink-muted)] italic">not declared</span>}</p>
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [err, setErr] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.getRequest(id);
      setRequest(r);
    } catch (e) {
      setErr(e.message);
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  if (err) return <main className="max-w-4xl mx-auto px-6 py-16"><p className="text-red-700" data-testid="detail-error">{err}</p></main>;
  if (!request) return <main className="max-w-4xl mx-auto px-6 py-16"><p data-testid="detail-loading" className="text-[var(--ink-muted)]">Loading…</p></main>;

  return (
    <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="request-detail-page">
      <Link to="/sponsor" className="inline-flex items-center gap-1.5 text-xs text-[var(--ink-secondary)] hover:text-[var(--ink-heading)] mb-8" data-testid="back-to-sponsor">
        <ArrowLeft size={14} /> Back to sponsor view
      </Link>

      <header className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
        <div className="lg:col-span-9">
          <p className="font-mono-ui text-xs text-[var(--ink-mono)] tracking-[0.14em]" data-testid="request-id">{request.id}</p>
          <h1 className="font-display text-3xl md:text-4xl text-[var(--ink-heading)] mt-2 leading-tight">{request.title}</h1>
          <p className="text-[var(--ink-secondary)] mt-4 text-lg leading-relaxed max-w-3xl">{request.purpose_statement}</p>
        </div>
        <div className="lg:col-span-3 flex flex-col lg:items-end gap-2">
          <StatusBadge status={request.status} />
          <span className="text-xs text-[var(--ink-muted)] font-mono-ui">Risk · {request.risk_level}</span>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <section className="lg:col-span-7 space-y-8">
          {/* Custody */}
          <div className="border border-slate-200 bg-white p-7" data-testid="custody-panel">
            <p className="eyebrow">Custody</p>
            <h3 className="font-display text-2xl text-[var(--ink-heading)] mt-1 mb-5">Who controls the evidence</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <LabelValue label="Dataset" value={request.dataset_label} />
              <LabelValue label="Source system" value={request.source_system} />
              <LabelValue label="Data steward" value={request.data_steward} />
              <LabelValue label="Policy reference" value={request.policy_id} />
            </div>
          </div>

          {/* Authority */}
          <div className="border border-slate-200 bg-white p-7" data-testid="authority-panel">
            <p className="eyebrow">Authority</p>
            <h3 className="font-display text-2xl text-[var(--ink-heading)] mt-1 mb-5">Who may decide</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <LabelValue label="Policy owner" value={request.policy_owner} />
              <LabelValue label="Assurance reviewer" value={request.assurance_reviewer} />
              <LabelValue label="Lawful basis declared" value={request.lawful_basis_declared} />
              <LabelValue label="Sponsor role" value={request.sponsor_role} />
            </div>
          </div>

          {/* Evidence boundary */}
          <div className="border border-slate-200 bg-white p-7" data-testid="evidence-panel">
            <p className="eyebrow">Evidence boundary</p>
            <h3 className="font-display text-2xl text-[var(--ink-heading)] mt-1 mb-2">What is shared, what is withheld</h3>
            <p className="text-sm text-[var(--ink-muted)] mb-5">
              Only references and hashes are surfaced. Raw evidence stays under
              local custody.
            </p>
            <div className="flex flex-wrap gap-2 mb-5">
              {(request.evidence_refs || []).map((e) => (
                <EvidenceChip key={e.ref_id} refId={e.ref_id} label={e.label} hash={e.artifact_hash} shared={e.shared} />
              ))}
            </div>
            <p className="text-xs text-[var(--ink-mono)] font-mono-ui">
              Withheld evidence · count {request.withheld_evidence_count}
            </p>
          </div>

          {/* AI advisory */}
          <div data-testid="advisory-section">
            <p className="eyebrow mb-3">Deterministic advisory</p>
            <AdvisoryNote>{request.advisory_note}</AdvisoryNote>
          </div>

          {/* Review actions */}
          <ReviewActions request={request} onChange={reload} />
        </section>

        <aside className="lg:col-span-5 space-y-8">
          <KatlasReceiptPanel receipts={request.receipts || []} />

          <section className="border border-slate-200 bg-white p-7" data-testid="timeline-panel">
            <p className="eyebrow">Review timeline</p>
            <h3 className="font-display text-2xl text-[var(--ink-heading)] mt-1 mb-5">What has happened</h3>
            <ReviewTimeline items={request.review_log || []} />
          </section>
        </aside>
      </div>
    </main>
  );
}
